import {groupBy, orderBy, uniq} from 'lodash'
import {useEffect} from 'react'
import {useMutationDataEditUpdateFields} from '~/apollo/queries/DataEditField'
import {
    SortField,
    useMutationDataEditAddNewProfiles,
    useMutationDataEditDeleteProfiles,
    useMutationDataEditUpdateValues,
    useQueryDataEditHiddenValues,
} from '~/apollo/queries/DataEditValue'
import {CurrentOrganisation} from '~/components/CurrentOrganisationContext'
import {
    DataEditField,
    DataEditValue,
    convertDataEditValuesByProfileIdStr,
    useOrganisationContext,
} from '~/components/OrganisationContext'
import {DataEditValueUpsert} from '~/objectTypes'
import {arrayify} from '~/util/array'
import {parseDateYYYYMMDD} from '~/util/date'
import {useTaskQueue} from './useTaskQueue'
import {toDict} from '~/util'
import {CellState, CellValue, Field, Loading, patchProfileRowIndexes, Profile} from './types'
import {usePhotosData} from '~/apollo/queries/Photos'
import {PhotosData} from '~/apollo/queries/__types__/PhotosData'

const DEBUG = 1

const fromDataEditField = (field: DataEditField): {idStr: string; field: Omit<Field, 'index'>} => ({
    idStr: field.idStr,
    field: {
        type: field.type,
        subTypes: field.subType?.split(';') || [],
        name: field.name,
        availableValues: field.availableValues,
        userCanEdit: field.userCanEdit === null ? undefined : field.userCanEdit,
    },
})
const patchDataEditFields = (
    dataEditFields: DataEditField[],
    fields: Field[],
    fieldIds: string[],
): (Omit<DataEditField, 'idStr'> & {idStr?: string})[] => {
    const updatingFieldOrder =
        dataEditFields.length === fields.length && fields.some((field, index) => field.index !== index)
    if (updatingFieldOrder) {
        return fields.map((field) => dataEditFields[field.index])
    }
    return [
        ...dataEditFields.map((f) => {
            const field = fields.find((field) => fieldIds[field.index] === f.idStr)
            return {
                ...f,
                idStr: field?.index === undefined ? f.idStr : fieldIds[field.index] || f.idStr,
                name: field?.name || f.name,
                type: field?.type as DataEditField['type'],
                subType: field?.subTypes?.join(';') || f.subType,
                availableValues: field?.availableValues || f.availableValues,
                userCanEdit: field?.userCanEdit === undefined ? null : field?.userCanEdit,
            }
        }),
        ...fields
            .filter((field) =>
                fieldIds[field.index]
                    ? !dataEditFields.some((f) => f.idStr === fieldIds[field.index])
                    : field.index >= dataEditFields.length,
            )
            .map((field) => ({
                idStr: fieldIds[field.index],
                name: field.name,
                type: field.type as DataEditField['type'],
                subType: field.subTypes?.join(';'),
                availableValues: field.availableValues,
                __typename: 'DataEditField' as 'DataEditField',
                organisationIdStr: dataEditFields[0]?.organisationIdStr,
                availableValueIdStrs: field.availableValues,
                availableValueLabels: field.availableValues,
                baseFields: null,
                userCanEdit: field?.userCanEdit === undefined ? null : field?.userCanEdit,
            })),
    ]
}

const calculateNames = (fields: Field[], profiles: Profile[]) => {
    const firstNameField = fields.find((field) => field.type === 'firstName')?.index
    const lastNameField = fields.find((field) => field.type === 'lastName')?.index
    const firstName = (profile: Profile) =>
        firstNameField !== undefined ? profile.cellValues?.[firstNameField]?.values[0] : undefined
    const lastName = (profile: Profile) =>
        lastNameField !== undefined ? profile.cellValues?.[lastNameField]?.values[0] : undefined
    const result = profiles.map((profile) => `${firstName(profile)} ${lastName(profile)}`)
    DEBUG && console.log('useData: calculateNames() [fields, profiles, result]', [fields, profiles, result])
    return result
}
const calculateReportsTo = (fields: Field[], profiles: Profile[], profileIdStrs: Record<string, number>) => {
    const reportsToField = fields.find((field) => field.name === 'Reports To')?.index
    const reportsTo = (profile: Profile) =>
        reportsToField !== undefined && profile.cellValues?.[reportsToField]?.values[0]
            ? profileIdStrs[profile.cellValues?.[reportsToField]?.values[0]]
            : undefined
    const result = profiles.map(reportsTo)
    DEBUG && console.log('useData: calculateReportsTo() [fields, profiles, result]', [fields, profiles, result])
    return result
}
const hasCircularReportsTo = (rowIndex: number, reportsTo: (number | undefined)[], path?: number[]): boolean => {
    if (reportsTo[rowIndex] === undefined) return false
    if (path?.includes(reportsTo[rowIndex]!)) return path?.[0] === reportsTo[rowIndex]
    return hasCircularReportsTo(reportsTo[rowIndex]!, reportsTo, [...(path || []), rowIndex])
}
const processCellStates = (
    cellValue: CellValue,
    field: Field | undefined,
    names: string[],
    reportsTo: (number | undefined)[],
): CellState => {
    if (!field || !cellValue.values.filter((value) => value).length)
        return {
            invalid: false,
            warning: false,
            displayValue: undefined,
        }
    return {
        invalid:
            field.type === 'date'
                ? !parseDateYYYYMMDD(cellValue.values[0])
                : field.name === 'Reports To'
                ? !names[cellValue.rowIndex] || hasCircularReportsTo(cellValue.rowIndex, reportsTo)
                : false,
        warning:
            field.name === 'Reports To'
                ? names.some((name, rowIndex) => rowIndex !== cellValue.rowIndex && name === names[cellValue.rowIndex])
                : false,
        displayValue:
            field.name === 'Reports To' && reportsTo[cellValue.rowIndex] !== undefined
                ? names[reportsTo[cellValue.rowIndex]!]
                : undefined,
    }
}
const processAllCellStates = (
    fields: Field[],
    profiles: Profile[],
    profileIdStrs: Record<string, number>,
): Profile[] => {
    const names = calculateNames(fields, profiles)
    const reportsTo = calculateReportsTo(fields, profiles, profileIdStrs)
    return Object.values(profiles).map((profile) => ({
        ...profile,
        cellValues: profile.cellValues.map((cellValue) => ({
            ...cellValue,
            cellState: processCellStates(cellValue, fields[cellValue.columnIndex], names, reportsTo),
        })),
    }))
}
const fromDataEditValues = (
    values: DataEditValue[],
    hiddenValues: DataEditValue[],
    fieldIds: string[],
    fields: Field[],
    photos?: PhotosData,
): {profiles: Profile[]; profileIds: string[]} => {
    const profiles: Profile[] = []
    const indexes: Record<string, number> = {}
    const profileIds: string[] = []

    const handleValue = (isHidden: boolean) => (value: DataEditValue) => {
        if (indexes[value.profileIdStr] === undefined) {
            indexes[value.profileIdStr] = Object.keys(indexes).length
            profileIds.push(value.profileIdStr)
        }
        const rowIndex = indexes[value.profileIdStr]
        if (!profiles[rowIndex]) {
            profiles[rowIndex] = {index: rowIndex, cellValues: []}
        }
        const columnIndex = fieldIds.indexOf(value.fieldOrParentCategoryIdStr)
        const cellValue = profiles[rowIndex].cellValues[columnIndex]

        profiles[rowIndex].cellValues[columnIndex] = {
            rowIndex,
            columnIndex,
            isHidden,
            values: value.valueIdStr
                ? [...(cellValue?.values || []), value.valueIdStr]
                : [...(cellValue?.values || []), value.value],
        }
    }

    values.forEach(handleValue(false))
    hiddenValues.forEach(handleValue(true))
    photos?.organisation?.profiles?.forEach((profile) => {
        const rowIndex = indexes[profile.idStr]
        if (!profiles[rowIndex]) {
            profiles[rowIndex] = {index: rowIndex, cellValues: []}
        }
        const columnIndex = 0
        profiles[rowIndex].cellValues[columnIndex] = {
            rowIndex,
            columnIndex,
            isHidden: false,
            values: !profile.photos?.[0]
                ? []
                : [profile.photos[0].idStr, profile.photos[0].originalUrl, profile.photos[0].profileUrl],
        }
    })

    return {
        profiles: processAllCellStates(
            fields,
            profiles,
            toDict(
                profileIds,
                (id) => id,
                (_, index) => index,
            ),
        ),
        profileIds,
    }
}

const toDataEditValueUpserts = (fieldIds: string[], profileIds: string[]) => (
    cellValues: CellValue[],
): DataEditValueUpsert[] =>
    cellValues.flatMap((cellValue) =>
        (cellValue.values.length ? cellValue.values : ['']).map((str) => ({
            fieldOrParentCategoryIdStr: fieldIds[cellValue.columnIndex],
            profileIdStr: profileIds[cellValue.rowIndex],
            value: cellValue.cellState?.displayValue || str,
            valueIdStr: str,
            organisationIdStr: null,
        })),
    )

const patchProfilesWithFieldOrderUpdate = (profiles: Profile[], fieldsReordered: Field[]): Profile[] =>
    profiles.map((profile) => ({
        ...profile,
        cellValues: fieldsReordered.map((field, newIndex) => {
            const cellValue = profile.cellValues?.find((cellValue) => cellValue?.columnIndex === field.index)
            return {
                columnIndex: newIndex,
                rowIndex: profile.index,
                values: cellValue?.values ?? [],
                isHidden: cellValue?.isHidden ?? false,
            }
        }),
    }))

export const useData = () => {
    const [foregroundQueue] = useTaskQueue<{
        loading: Loading
        fields: Field[]
        fieldIds: string[]
        profiles: Profile[]
        profileIds: string[]
        sortField?: SortField
    }>({loading: {grid: true}, fields: [], fieldIds: [], profiles: [], profileIds: []}) // quick operations, state updates
    const [backgroundQueue] = useTaskQueue({}) // long running operations, server writes

    const [dataEditFields, setDataEditFields] = useOrganisationContext.useDataEditFields()
    const {currentOrganisation} = CurrentOrganisation.useContainer()
    const [visibleValues, setVisibleValues] = useOrganisationContext.useDataEditValues()
    const [, setVisibleValuesIndexed] = useOrganisationContext.useDataEditValuesByProfileIdStr()
    const hiddenValuesQuery = useQueryDataEditHiddenValues(currentOrganisation!.idStr)
    const photosQuery = usePhotosData(currentOrganisation!.idStr)

    const [, callUpdateFields] = useMutationDataEditUpdateFields(currentOrganisation!.idStr)
    const [, callUpdateValues] = useMutationDataEditUpdateValues(currentOrganisation!.idStr)
    const [, callAddNewProfiles] = useMutationDataEditAddNewProfiles(currentOrganisation!.idStr)
    const [, callDeleteProfiles] = useMutationDataEditDeleteProfiles(currentOrganisation!.idStr)

    const loading = () => foregroundQueue.taskState.loading
    const fields = () => foregroundQueue.taskState.fields
    const fieldIds = () => foregroundQueue.taskState.fieldIds
    const profiles = () => foregroundQueue.taskState.profiles
    const profileIds = () => foregroundQueue.taskState.profileIds
    const sortField = () => foregroundQueue.taskState.sortField
    const setLoading = (loading: Loading) => foregroundQueue.setTaskState({loading})
    const setFields = (fields: Field[]) => foregroundQueue.setTaskState({fields})
    const setFieldIds = (fieldIds: string[]) => foregroundQueue.setTaskState({fieldIds})
    const setProfileIds = (profileIds: string[]) => foregroundQueue.setTaskState({profileIds})

    useEffect(() => {
        if (foregroundQueue.taskState.fields.length) return
        foregroundQueue.enqueue(async () => {
            DEBUG && console.log('useData: useEffect() [dataEditFields]')
            const fields = [
                {
                    idStr: 'photo',
                    field: {
                        type: 'photo',
                        subTypes: [],
                        name: 'Photo',
                        availableValues: [],
                        userCanEdit: false,
                    },
                },
                ...dataEditFields.map(fromDataEditField),
            ]
            return {
                fields: fields.map((field, index) => ({...field.field, index})),
                fieldIds: fields.map((field) => field.idStr),
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataEditFields])

    useEffect(() => {
        DEBUG &&
            console.log(
                'useData: useEffect() [hiddenValuesQuery.loading, photosQuery.loading, photosQuery.data]',
                [hiddenValuesQuery.loading, photosQuery.loading, photosQuery.data],
                'dataEditFields',
                dataEditFields,
            )

        if (hiddenValuesQuery.loading || photosQuery.loading || !photosQuery.data) return

        foregroundQueue.enqueue(async ({fieldIds, fields}) => {
            const values = fromDataEditValues(
                visibleValues,
                hiddenValuesQuery.data?.dataEditValues || [],
                fieldIds,
                fields,
                photosQuery.data,
            )
            updateLocalValues(toDict(values.profiles), {
                removeMissingValues: true,
                onComplete: () => setLoading({}),
                callerPath: ['useEffect() hiddenValuesQuery.loading, photosQuery.loading, photosQuery.data'],
            })
            return {profileIds: values.profileIds}
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hiddenValuesQuery.loading, photosQuery.loading, photosQuery.data])

    const updateLocalValues = (
        newProfiles: Record<number, Profile | null | undefined>,
        options?: {
            removeMissingValues?: boolean
            onComplete?: () => void
            callerPath?: string[]
        },
    ) => {
        DEBUG && console.log('useData: updateLocalValues() [newProfiles, options]', [newProfiles, options])
        foregroundQueue.enqueue(async ({profiles, profileIds, fields, fieldIds}) => {
            DEBUG && console.log('useData: updateLocalValues() [profiles]', [profiles])
            let patched: Profile[] = []
            ;[...profiles, ...Object.values(newProfiles)].forEach((profile) => {
                if (!profile || newProfiles[profile.index] === null) return
                profile?.cellValues?.forEach?.((cellValue) => {
                    if (!patched[profile.index]) {
                        patched[profile.index] = {index: profile.index, cellValues: []}
                    }
                    const previousValue = patched[profile.index].cellValues[cellValue.columnIndex]
                    if (options?.removeMissingValues) {
                        patched[profile.index].cellValues[cellValue.columnIndex] = cellValue
                    } else {
                        patched[profile.index].cellValues[cellValue.columnIndex] = {
                            ...cellValue,
                            values: uniq([...(previousValue?.values || []), ...(cellValue?.values || [])]),
                            cellState: {
                                ...previousValue?.cellState,
                                ...cellValue.cellState,
                            },
                        }
                    }
                })
            })
            DEBUG && console.log('useData: updateLocalValues() [patched1]', [patched])
            patched = patched.filter((profile) => profile).map(patchProfileRowIndexes(0))
            DEBUG && console.log('useData: updateLocalValues() [patched3]', [patched])

            const newDataEditValues = patched
                .flatMap((profile) => profile.cellValues)
                .filter((cellValue) => !cellValue.isHidden)
                .flatMap((cellValue) =>
                    (cellValue.values.length ? cellValue.values : []).map((str) => ({
                        fieldOrParentCategoryIdStr: fieldIds[cellValue.columnIndex],
                        profileIdStr: profileIds[cellValue.rowIndex],
                        value: cellValue.cellState?.displayValue || str,
                        valueIdStr: str,
                        organisationIdStr: currentOrganisation!.idStr,
                        __typename: 'DataEditValue',
                    })),
                ) as DataEditValue[]

            setVisibleValues(newDataEditValues)
            setVisibleValuesIndexed(convertDataEditValuesByProfileIdStr(newDataEditValues))

            const processed = processAllCellStates(
                fields,
                patched,
                toDict(
                    profileIds,
                    (id) => id,
                    (_, index) => index,
                ),
            )
            options?.onComplete &&
                foregroundQueue.enqueue(async () => {
                    options?.onComplete?.()
                    return {}
                })

            DEBUG &&
                console.log('useData: updateLocalValues() done [patched, processed, newDataEditValues, profileIds]', [
                    patched,
                    processed,
                    newDataEditValues,
                    profileIds,
                ])

            return {profiles: processed}
        })
    }

    const updateLocalFields = (
        newFields: Field[],
        options?: {
            skipValues?: boolean
            fieldOrderUpdated?: boolean
            removeMissingFields?: boolean
            onComplete?: (updatedFields: Field[]) => void
            onValuesChanged?: () => void
            callerPath?: string[]
        },
    ) => {
        foregroundQueue.enqueue(async ({loading, fieldIds, fields, profiles, profileIds}) => {
            DEBUG &&
                console.log(
                    'useData: updateLocalFields() [newFields, fieldIds, options, fields, loading, profiles, profileIds]',
                    [newFields, fieldIds, options, fields, loading, profiles, profileIds],
                )

            const patchedFields = patchDataEditFields(dataEditFields, newFields, fieldIds)
            setDataEditFields(patchedFields as DataEditField[])

            const fieldsReordered =
                options?.removeMissingFields && options?.fieldOrderUpdated
                    ? newFields
                    : [
                          ...fields
                              .map(
                                  (field) =>
                                      newFields.find((f) => f.index === field.index) ??
                                      (options?.removeMissingFields ? undefined : field),
                              )
                              .filter((field) => field)
                              .map((field) => field!),
                          ...newFields.filter((field) => field.index >= fields.length),
                      ]
            const fieldsPatched = fieldsReordered.map((field, index) => ({...field, index}))

            if (options?.fieldOrderUpdated) {
                updateLocalValues(toDict(patchProfilesWithFieldOrderUpdate(profiles, fieldsReordered)), {
                    removeMissingValues: true,
                    onComplete: () => {
                        options?.onValuesChanged?.()
                        options?.onComplete &&
                            foregroundQueue.enqueue(async () => {
                                options?.onComplete?.(fieldsPatched)
                                return {}
                            })
                    },
                    callerPath: [...(options?.callerPath || []), 'updateLocalFields() fieldOrderUpdated'],
                })
            } else if (!options?.skipValues) {
                const recalculatedProfiles = fromDataEditValues(
                    visibleValues,
                    hiddenValuesQuery.data?.dataEditValues || [],
                    fieldIds,
                    fieldsPatched,
                    photosQuery.data,
                )
                const newProfiles = profileIds
                    .map(
                        (profileIdStr) =>
                            recalculatedProfiles.profiles[recalculatedProfiles.profileIds.indexOf(profileIdStr)],
                    )
                    .map(patchProfileRowIndexes(0))

                DEBUG &&
                    console.log(
                        'useData: updateLocalFields() !options?.skipValues [visibleValues, hiddenValues, recalculatedProfiles, newProfiles, fieldIds, fieldsPatched]',
                        [
                            visibleValues,
                            hiddenValuesQuery.data?.dataEditValues,
                            recalculatedProfiles,
                            newProfiles,
                            fieldIds,
                            fieldsPatched,
                        ],
                    )
                updateLocalValues(newProfiles, {
                    ...options,
                    onComplete: () => {
                        options?.onValuesChanged?.()
                        options?.onComplete &&
                            foregroundQueue.enqueue(async () => {
                                options?.onComplete?.(fieldsPatched)
                                return {}
                            })
                    },
                    callerPath: [...(options?.callerPath || []), 'updateLocalFields() !options?.skipValues'],
                })
            } else {
                options?.onComplete &&
                    foregroundQueue.enqueue(async () => {
                        options?.onComplete?.(fieldsPatched)
                        return {}
                    })
            }

            DEBUG && console.log('useData: updateLocalFields() setFields [old, new]', [fields, fieldsPatched])

            return {fields: fieldsPatched}
        })
    }

    const _setSortField = (field: SortField) => {
        foregroundQueue.enqueue(async ({loading, profiles, sortField, profileIds}) => {
            DEBUG && console.log('useData: setSortField() [field]', [field])
            if (loading.deletingRowIndexes?.length || loading.addingRowIndexes?.length) {
                return {}
            }
            if (field !== undefined) {
                const newProfiles = orderBy(
                    profiles.map((profile) => ({profile, idStr: profileIds[profile.index]})),
                    (profile) => {
                        const rawValue = profile.profile.cellValues?.[field.columnIndex]?.values?.[0]
                        return (rawValue === undefined || rawValue === '') && field.direction !== 'desc'
                            ? 'ZZZZZZ'
                            : rawValue === undefined || rawValue === ''
                            ? ''
                            : isNaN(parseFloat(rawValue))
                            ? rawValue.toUpperCase()
                            : rawValue.padStart(20, '0')
                    },
                    arrayify(field.direction),
                ).map((profile, index) => ({
                    profile: {
                        index,
                        cellValues: profile.profile.cellValues.map((cellValue) => ({...cellValue, rowIndex: index})),
                    },
                    idStr: profile.idStr,
                }))
                DEBUG && console.log('useData: setSortField() done [newProfiles]', [newProfiles])
                return {
                    profiles: newProfiles.map((profile) => profile.profile),
                    profileIds: newProfiles.map((profile) => profile.idStr),
                    sortField: field,
                }
            } else {
                DEBUG && console.log('useData: setSortField() done')
                return {sortField: field}
            }
        })
    }

    const updateFieldsAtServer = (
        fieldsUpdate: Field[],
        fieldIds: string[],
        options?: {
            removeMissingFields?: boolean
            onComplete?: (updatedFields: DataEditField[]) => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        DEBUG &&
            console.log('useData: updateFieldsAtServer() [fieldsUpdate, fieldIds, options]', [
                fieldsUpdate,
                fieldIds,
                options,
            ])
        backgroundQueue.enqueue(async () => {
            try {
                const {fields: updatedFields} = await callUpdateFields({
                    dataEditFields: fieldsUpdate
                        .filter((field) => field.index > 0)
                        .map((field) => ({
                            idStr: fieldIds[field.index] || null,
                            name: field.name,
                            type: field.type as DataEditField['type'],
                            subType: field.subTypes?.join(';'),
                            availableValues: field.availableValues,
                            organisationIdStr: currentOrganisation!.idStr,
                            usersCanCreateValues: null,
                            userCanEdit: field.userCanEdit === undefined ? null : field.userCanEdit,
                        })),
                    removeMissingFields: options?.removeMissingFields || false,
                })
                options?.onComplete &&
                    foregroundQueue.enqueue(async () => {
                        options?.onComplete?.(updatedFields)
                        return {}
                    })
            } catch (error) {
                DEBUG && console.log(error)
                options?.onServerError &&
                    foregroundQueue.enqueue(async () => {
                        options?.onServerError?.(error)
                        return {}
                    })
            }
            return {}
        })
    }

    const addFields = (
        fieldsUpdate: Field[],
        options?: {
            onComplete?: (addedFields: Field[], newFieldIds: string[]) => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        foregroundQueue.enqueue(async ({loading, fields, fieldIds}) => {
            DEBUG && console.log('useData: addFields() [fieldsUpdate]', [fieldsUpdate])
            if (!fieldsUpdate.length) {
                options?.onComplete?.([], fieldIds)
                return {}
            }
            if (loading.deletingRowIndexes?.length || loading.addingRowIndexes?.length) {
                addFields(fieldsUpdate, options)
                return {}
            }
            const originalFields = fields.slice()
            updateLocalFields(fieldsUpdate, {
                fieldOrderUpdated: true,
                onComplete: () =>
                    updateFieldsAtServer(fieldsUpdate, fieldIds, {
                        onComplete: (updatedFields) => {
                            const addedFields = updatedFields
                                .map((field, index) => ({...fromDataEditField(field).field, idStr: field.idStr, index}))
                                .filter((field) => !fieldIds.includes(field.idStr))
                            const newFieldIds = [...fieldIds, ...addedFields.map((field) => field.idStr)]
                            DEBUG &&
                                console.log(
                                    'useData: addFields() addFieldsAtServer.setFieldIds [addedFields, newFieldIds]',
                                    [addedFields, newFieldIds],
                                )
                            setFieldIds(newFieldIds)
                            options?.onComplete?.(addedFields, newFieldIds)
                        },
                        onServerError: (error) => {
                            DEBUG && console.log('useData: addFields() Rollback fields')
                            updateLocalFields(originalFields, {
                                fieldOrderUpdated: true,
                                callerPath: [...(options?.callerPath || []), 'addFields/onServerError'],
                            })
                            options?.onServerError?.(error)
                        },
                        callerPath: [...(options?.callerPath || []), 'addFields'],
                    }),
                callerPath: [...(options?.callerPath || []), 'addFields'],
            })
            return {}
        })
    }

    // Fields must exist already
    // Fields can be deleted here
    const updateFields = (
        fieldsUpdate: Field[],
        options?: {
            removeMissingFields?: boolean
            skipValues?: boolean
            onComplete?: (updatedFields: Field[]) => void
            onFieldsChanged?: (updatedFields: Field[]) => void
            onValuesChanged?: () => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        foregroundQueue.enqueue(async ({loading, fields, fieldIds, profiles}) => {
            if (!fieldsUpdate.length) {
                options?.onComplete?.([])
                return {}
            }
            if (loading.deletingRowIndexes?.length || loading.addingRowIndexes?.length) {
                updateFields(fieldsUpdate, options)
                return {}
            }
            DEBUG && console.log('useData: updateFields() [fieldsUpdate, options]', [fieldsUpdate, options])

            const originalFields = fields
            const originalProfiles = profiles
            const fieldOrderUpdated =
                (options?.removeMissingFields || fields.length === fieldsUpdate.length) &&
                fields.map((field) => field.index).join(';') !== fieldsUpdate.map((field) => field.index).join(';')
                    ? true
                    : false

            updateLocalFields(fieldsUpdate, {
                fieldOrderUpdated,
                skipValues: options?.skipValues,
                removeMissingFields: options?.removeMissingFields,
                onValuesChanged: options?.onValuesChanged,
                onComplete: (updatedFields) => {
                    options?.onFieldsChanged?.(updatedFields)
                    updateFieldsAtServer(fieldsUpdate, fieldIds, {
                        removeMissingFields: options?.removeMissingFields,
                        onComplete: (updatedFields) => {
                            const newFields = [
                                {
                                    idStr: 'photo',
                                    type: 'photo',
                                    subTypes: [],
                                    name: 'Photo',
                                    availableValues: [],
                                    userCanEdit: false,
                                },
                                ...updatedFields.map((field) => ({
                                    ...fromDataEditField(field).field,
                                    idStr: field.idStr,
                                })),
                            ].map((field, index) => ({...field, index}))
                            if (fieldOrderUpdated) {
                                const newFieldIds = newFields.map((field) => field.idStr)
                                DEBUG &&
                                    console.log(
                                        'useData: updateFields() updateFieldsAtServer.setFieldIds [newFields, newFieldIds]',
                                        [newFields, newFieldIds],
                                    )
                                setFieldIds(newFieldIds)
                            }
                            options?.onComplete?.(newFields)
                        },
                        onServerError: (error) => {
                            DEBUG &&
                                console.log('useData: updateFields() Rollback fields, skipValues=', options?.skipValues)
                            setFields(originalFields)
                            options?.onFieldsChanged?.(originalFields)
                            if (!options?.skipValues) {
                                updateLocalValues(toDict(originalProfiles), {
                                    onComplete: () => {
                                        options?.onValuesChanged?.()
                                        options?.onServerError?.(error)
                                    },
                                    callerPath: [...(options?.callerPath || []), 'updateFields/onServerError'],
                                })
                            } else {
                                options?.onServerError?.(error)
                            }
                        },
                        callerPath: [...(options?.callerPath || []), 'updateFields'],
                    })
                },
                callerPath: [...(options?.callerPath || []), 'updateFields'],
            })
            return {}
        })
    }

    const addNewProfilesAtServer = (
        addProfiles: Profile[],
        profiles: Profile[],
        fieldIds: string[],
        options?: {
            onComplete?: (newProfileIds: {rowIndex: number; idStr: string}[]) => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        DEBUG && console.log('useData: addNewProfilesAtServer() [addProfiles, options]', [addProfiles, options])
        backgroundQueue.enqueue(async () => {
            try {
                const addedValues = await callAddNewProfiles({
                    newProfiles: addProfiles.map((profile) =>
                        profile.cellValues
                            .filter((cellValue) => cellValue.columnIndex > 0)
                            .flatMap((cellValue) =>
                                cellValue.values.map((value) => ({
                                    fieldOrParentCategoryIdStr: fieldIds[cellValue.columnIndex],
                                    value,
                                    organisationIdStr: currentOrganisation!.idStr,
                                })),
                            ),
                    ),
                })
                options?.onComplete &&
                    foregroundQueue.enqueue(async () => {
                        const serverValuesByProfile = groupBy(addedValues, (profile) => profile.profileIdStr)
                        const _addedProfiles = Object.keys(serverValuesByProfile).map((profileIdStr, index) => ({
                            idStr: profileIdStr,
                            rowIndex: index + profiles.length,
                        }))
                        options?.onComplete?.(_addedProfiles)
                        return {}
                    })
            } catch (error) {
                console.log(error)
                options?.onServerError &&
                    foregroundQueue.enqueue(async () => {
                        options?.onServerError?.(error)
                        return {}
                    })
            }
            return {}
        })
    }

    // Appends to bottom, does not apply sorting
    const addNewProfiles = (
        addProfiles: Profile[],
        options: {
            onComplete?: () => void
            onValuesUpdated?: () => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        foregroundQueue.enqueue(async ({loading, profiles}) => {
            if (!addProfiles.length) {
                options?.onComplete?.()
                return {}
            }
            if (
                loading.deletingRowIndexes?.length ||
                loading.columnIndexes?.length ||
                loading.addingRowIndexes?.length
            ) {
                addNewProfiles(addProfiles, options)
                return {}
            }
            DEBUG && console.log('useData: addNewProfiles() [addProfiles, options]', [addProfiles, options])

            const originalProfileCount = profiles.length + (loading.addingRowIndexes?.length ?? 0)

            foregroundQueue.enqueue(async ({profiles, profileIds, fieldIds}) => {
                const patchedProfiles = addProfiles
                    .map(patchProfileRowIndexes(originalProfileCount))
                    .map((profile) => ({...profile, idStr: `${profile.index}`}))
                const revertProfiles = toDict(
                    patchedProfiles,
                    (profile) => profile!.index,
                    () => null,
                )
                const createProfiles = toDict(patchedProfiles, (profile) => profile.index)

                updateLocalValues(createProfiles, {
                    onComplete: () => {
                        options?.onValuesUpdated?.()
                        addNewProfilesAtServer(addProfiles, profiles, fieldIds, {
                            onComplete: (newProfileIds) => {
                                DEBUG &&
                                    console.log(
                                        'useData: addNewProfiles() addNewProfilesAtServer.onComplete [newProfileIds]',
                                        [newProfileIds],
                                    )
                                const copy = profileIds.slice()
                                newProfileIds.forEach((profileId) => {
                                    copy[profileId.rowIndex] = profileId.idStr
                                })
                                setProfileIds(copy)
                                setLoading({
                                    addingRowIndexes: [],
                                })
                                options?.onComplete?.()
                            },
                            onServerError: (error) => {
                                console.log('useData: addNewProfiles() Rollback values')
                                updateLocalValues(revertProfiles, {
                                    onComplete: () => {
                                        setLoading({
                                            addingRowIndexes: [],
                                        })
                                        options?.onValuesUpdated?.()
                                        options?.onServerError?.(error)
                                    },
                                    callerPath: [...(options?.callerPath || []), 'addNewProfiles/onServerError'],
                                })
                            },
                            callerPath: [...(options?.callerPath || []), 'addNewProfiles'],
                        })
                    },
                    callerPath: [...(options?.callerPath || []), 'addNewProfiles'],
                })

                return {}
            })

            DEBUG &&
                console.log('useData: addNewProfiles() done [originalProfileCount, loading.addingRowIndexes]', [
                    originalProfileCount,
                    loading.addingRowIndexes,
                ])
            return {loading: {addingRowIndexes: [...(loading.addingRowIndexes || []), originalProfileCount]}}
        })
    }

    const deleteProfilesAtServer = (
        profileIdStrs: string[],
        options: {
            onComplete?: () => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        DEBUG && console.log('useData: deleteProfilesAtServer() [profileIdStrs, options]', [profileIdStrs, options])
        backgroundQueue.enqueue(async () => {
            try {
                await callDeleteProfiles({profileIdStrs})
                options?.onComplete &&
                    foregroundQueue.enqueue(async () => {
                        options?.onComplete?.()
                        return {}
                    })
            } catch (error) {
                console.log(error)
                options?.onServerError &&
                    foregroundQueue.enqueue(async () => {
                        options?.onServerError?.(error)
                        return {}
                    })
            }
            return {}
        })
    }

    const deleteProfiles = (
        rowIndexes: number[],
        options: {
            onComplete?: () => void
            onValuesUpdated?: () => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        foregroundQueue.enqueue(async ({loading, profileIds}) => {
            if (
                loading.addingRowIndexes?.length ||
                loading.deletingRowIndexes?.length ||
                loading.columnIndexes?.length
            ) {
                deleteProfiles(rowIndexes, options)
                return {}
            }
            DEBUG && console.log('useData: deleteProfiles() [rowIndexes, options]', [rowIndexes, options])

            const originalProfileIds = profileIds.slice()

            const newProfileIds = profileIds
                .map((idStr, index) => ({idStr, index}))
                .filter((pair) => !rowIndexes.includes(pair.index))
                .map((pair) => pair.idStr)

            foregroundQueue.enqueue(async ({profiles}) => {
                const baseIndex = Math.min(...rowIndexes)
                const revertProfiles = toDict(profiles.slice(baseIndex), (profile) => profile.index)
                const removeProfiles = toDict(
                    rowIndexes.map((rowIndex) => profiles[rowIndex]),
                    (profile) => profile!.index,
                    () => null,
                )

                updateLocalValues(removeProfiles, {
                    onComplete: () => {
                        options?.onValuesUpdated?.()
                        deleteProfilesAtServer(
                            rowIndexes
                                .filter((rowIndex) => originalProfileIds[rowIndex])
                                .map((rowIndex) => originalProfileIds[rowIndex]),
                            {
                                onComplete: () => {
                                    setLoading({deletingRowIndexes: []})
                                    options?.onComplete?.()
                                },
                                onServerError: (error) => {
                                    DEBUG && console.log('useData: deleteProfiles() Rollback values')

                                    setProfileIds(originalProfileIds)
                                    updateLocalValues(revertProfiles, {
                                        onComplete: () => {
                                            setLoading({deletingRowIndexes: []})
                                            options?.onValuesUpdated?.()
                                            options?.onServerError?.(error)
                                        },
                                        callerPath: [...(options?.callerPath || []), 'deleteProfiles/onServerError'],
                                    })
                                },
                                callerPath: [...(options?.callerPath || []), 'deleteProfiles'],
                            },
                        )
                    },
                    callerPath: [...(options?.callerPath || []), 'deleteProfiles'],
                })
                return {}
            })
            return {loading: {deletingRowIndexes: rowIndexes}, profileIds: newProfileIds}
        })
    }

    const updateValuesAtServer = (
        newProfiles: Profile[],
        fieldIds: string[],
        profileIds: string[],
        options?: {
            removeMissingValues?: boolean
            onComplete?: () => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        DEBUG && console.log('useData: updateValuesAtServer() [newProfiles, options]', [newProfiles, options])
        backgroundQueue.enqueue(async () => {
            try {
                await callUpdateValues({
                    dataEditValues: toDataEditValueUpserts(
                        fieldIds,
                        profileIds,
                    )(
                        newProfiles.flatMap((profile) =>
                            profile.cellValues.filter((cellValue) => cellValue.columnIndex > 0),
                        ),
                    ),
                    removeMissingValues: options?.removeMissingValues || false,
                })
                options?.onComplete &&
                    foregroundQueue.enqueue(async () => {
                        options?.onComplete?.()
                        return {}
                    })
            } catch (error) {
                console.log(error)
                options?.onServerError &&
                    foregroundQueue.enqueue(async () => {
                        options?.onServerError?.(error)
                        return {}
                    })
            }
            return {}
        })
    }

    // Fields and profiles must exist already.
    // We are also not deleting profiles or fields here.
    // Sorting would need to be applied separately
    const updateValues = (
        newProfiles: Profile[],
        options?: {
            removeMissingValues?: boolean
            onComplete?: () => void
            onValuesUpdated?: () => void
            onServerError?: (error: any) => void
            callerPath?: string[]
        },
    ) => {
        foregroundQueue.enqueue(async ({loading, profiles, fieldIds, profileIds}) => {
            DEBUG &&
                console.log('useData: updateValues() [newProfiles, options, loading]', [newProfiles, options, loading])

            if (
                loading.deletingRowIndexes?.length ||
                loading.columnIndexes?.length ||
                loading.addingRowIndexes?.length
            ) {
                updateValues(newProfiles, options)
                return {}
            }

            const revertProfiles = toDict(
                newProfiles,
                (profile) => profile.index,
                (profile) => ({
                    ...profiles[profile.index],
                    cellValues: profile.cellValues.map((cellValue) => ({
                        ...profiles[profile.index].cellValues[cellValue.columnIndex],
                    })),
                }),
            )
            const updateProfiles = toDict(newProfiles, (profile) => profile.index)

            updateLocalValues(updateProfiles, {
                removeMissingValues: options?.removeMissingValues,
                onComplete: () => {
                    options?.onValuesUpdated?.()
                    updateValuesAtServer(newProfiles, fieldIds, profileIds, {
                        removeMissingValues: options?.removeMissingValues,
                        onComplete: options?.onComplete,
                        onServerError: (error) => {
                            DEBUG && console.log('useData: updateValues() Rollback values')
                            updateLocalValues(revertProfiles, {
                                removeMissingValues: true,
                                onComplete: () => {
                                    options?.onValuesUpdated?.()
                                    options?.onServerError?.(error)
                                },
                                callerPath: [...(options?.callerPath || []), 'updateValues/onServerError'],
                            })
                        },
                        callerPath: [...(options?.callerPath || []), 'updateValues'],
                    })
                },
                callerPath: [...(options?.callerPath || []), 'updateValues'],
            })

            return {}
        })
    }

    const pasteValues = (
        startRowIndex: number,
        startColIndex: number,
        values: string[][][],
        options?: {onComplete?: () => void; onServerError?: () => void},
    ) => {
        foregroundQueue.enqueue(async ({loading, fields, profiles}) => {
            if (
                loading.deletingRowIndexes?.length ||
                loading.columnIndexes?.length ||
                loading.addingRowIndexes?.length
            ) {
                pasteValues(startRowIndex, startColIndex, values, options)
                return {}
            }

            const fieldsToCreate = [...new Array(Math.max(0, startColIndex + values[0].length - fields.length))].map(
                (_, index) => ({
                    index: index + fields.length,
                    name: `Column ${index + 1 + fields.length}`,
                    type: 'text',
                    subTypes: [],
                    organisationIdStr: currentOrganisation!.idStr,
                    availableValues: [],
                }),
            )
            const fieldsToUpdate = fields
                .slice(startColIndex, startColIndex + values[0].length)
                .map((field, index) => {
                    const newAvailableValues =
                        field.type === 'category'
                            ? uniq([...field.availableValues, ...values.flatMap((row) => row[index])])
                            : []
                    return newAvailableValues.join('|') === field.availableValues.join('|')
                        ? null
                        : {...field, availableValues: newAvailableValues}
                })
                .filter((field) => field)
                .map((field) => field!)

            const affectedFields = [...fields.slice(startColIndex, startColIndex + values[0].length), ...fieldsToCreate]
            const profilesToUpdate = profiles
                .slice(startRowIndex, startRowIndex + values.length)
                .map((profile, profileIndex) => ({
                    index: profile.index,
                    cellValues: affectedFields.map((field, fieldIndex) => ({
                        columnIndex: field.index,
                        rowIndex: profile.index,
                        values: values[profileIndex][fieldIndex],
                    })),
                }))
            const profilesToCreate = values.slice(profilesToUpdate.length).map((row, profileIndex) => ({
                index: profilesToUpdate.length + profileIndex,
                cellValues: affectedFields.map((field, fieldIndex) => ({
                    columnIndex: field.index,
                    rowIndex: profilesToUpdate.length + profileIndex,
                    values: row[fieldIndex],
                })),
            }))

            addFields(fieldsToCreate, {
                onComplete: () => {
                    updateFields(fieldsToUpdate, {
                        skipValues: true,
                        onComplete: () => {
                            addNewProfiles(profilesToCreate, {
                                onComplete: () => {
                                    updateValues(profilesToUpdate, {
                                        removeMissingValues: true,
                                        onComplete: options?.onComplete,
                                        onServerError: options?.onServerError,
                                    })
                                },
                                onServerError: options?.onServerError,
                            })
                        },
                        onServerError: options?.onServerError,
                    })
                },
                onServerError: options?.onServerError,
            })
            return {}
        })
    }

    return {
        loading,
        fields,
        fieldIds,
        profiles,
        profileIds,
        sortField,
        setSortField: _setSortField,
        addFields,
        updateFields,
        addNewProfiles,
        deleteProfiles,
        updateValuesAtServer,
        updateValues,
        calculateNames,
        pasteValues,
    }
}
